"use client";
import { ExpandLess, ExpandMore, Menu, Person } from "@mui/icons-material";
import {
  AppBar,
  Box,
  Button,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu as MuiMenu,
  MenuItem,
  Paper,
  CircularProgress,
  Toolbar,
  Typography,
} from "@mui/material";
import { Suspense, useCallback, useEffect, useState } from "react";
import { navItems } from "../components/NavItems";

import LSLogoStretched from "../images/StretchedLogo.svg";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import useScreenDimensions from "../hooks/useScreenDimensions";
import {
  drawerWidthLarge,
  drawerWidthSmall,
  localStorageKeys,
  urlRoutes,
  defaultUserPermissions,
  includeSideMenu,
  adminPages,
  reportingPages,
  comingSoonPages,
  assetsPages,
  vendorPages,
} from "../constants/page";
import { useDispatch, useSelector } from "react-redux";
import { setPermissions } from "../redux/permissions";
import { setUser } from "../redux/user";
import { blue, green, yellow } from "../colors";
import Login from "../routes/login";
import ImpersonationModal from "../modals/ImpersonationModal";
import { customAccessLabel } from "../redux/impersonation";
import useCompanyCatalog from "../hooks/useCompanyCatalog";
import GlobalModals from "../modals/GlobalModals";
import { apiGetAuth, handleLogout } from "../calls/apiCalls";
import { GET_MY_NOTIFICATIONS, GET_MY_TASKS } from "../constants/apiUrls";
import InboxBell from "../modals/InboxBell";
import UserAvatar from "../components/cards/UserAvatar";

const accessiblePaths = [
  urlRoutes.forgotPassword,
  urlRoutes.setPassword,
  urlRoutes.signup,
  urlRoutes.form,
];

function AppLayout({ children, topComponents = [] }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [showSideMenu, setShowSideMenu] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMenuItem, setActiveMenuItem] = useState(null);
  const [activeSideMenuItem, setActiveSideMenuItem] = useState(null);
  const permissions = useSelector((state) => state.permissions);
  const { user } = useSelector((state) => state.user);
  const { users, status: usersStatus } = useSelector((state) => state.users);
  const impersonation = useSelector((state) => state.impersonation);
  const [impersonationOpen, setImpersonationOpen] = useState(false);
  useCompanyCatalog("users", { enabled: impersonationOpen });
  const [searchParams, setSearchParams] = useSearchParams();
  const items = navItems(permissions);
  const accessiblePage =
    accessiblePaths.findIndex((c) => c === window.location.pathname) >= 0;

  const [profileAnchorEl, setProfileAnchorEl] = useState(null);

  const getSideMenu = () => {
    let menuIdx = null;
    if (adminPages.findIndex((c) => c === location.pathname) >= 0) {
      menuIdx = items.findIndex((c) => c.name === "Admin");
    } else if (reportingPages.findIndex((c) => c === location.pathname) >= 0) {
      menuIdx = items.findIndex((c) => c.name === "Reporting");
    } else if (assetsPages.findIndex((c) => c === location.pathname) >= 0) {
      menuIdx = items.findIndex((c) => c.name === "Assets");
    } else if (vendorPages.findIndex((c) => c === location.pathname) >= 0) {
      menuIdx = items.findIndex((c) => c.name === "Vendors");
    } else if (comingSoonPages.findIndex((c) => c === location.pathname) >= 0) {
      menuIdx = items.findIndex((c) => c.name === "Coming Soon");
    }
    return menuIdx === null ? items : items?.[menuIdx]?.subItems;
  };

  const [appPages, setAppPages] = useState(getSideMenu());
  const { isMobile, isTablet } = useScreenDimensions();
  const profileMenuOpen = Boolean(profileAnchorEl);

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  const handleNavigate = (path) => {
    navigate(path);
    setProfileAnchorEl(null);
  };

  const handleMouseEnter = (item) => {
    setActiveMenuItem(item);
  };

  const handleMouseLeave = () => {
    setActiveMenuItem(null);
  };

  const handleSideMenuItemClick = (item) => {
    setActiveSideMenuItem((prevState) =>
      prevState?.name === item?.name ? null : item,
    );
  };

  const handleImpersonateClick = () => {
    setImpersonationOpen(true);
  };

  const openVendor = () => {
    if (!user) return;
    searchParams.set("user", user.uuid);
    setSearchParams(searchParams);
    setProfileAnchorEl(null);
  };

  const handleOpenProfile = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleCloseProfile = () => {
    setProfileAnchorEl(null);
  };

  useEffect(() => {
    if (
      includeSideMenu.findIndex((c) => c === location.pathname) < 0 ||
      !user?.uuid
    ) {
      setShowSideMenu(false);
    } else {
      setAppPages(getSideMenu());
      setShowSideMenu(true);
    }
  }, [permissions, location.pathname, user?.uuid]);

  const drawer = (
    <Box sx={{ textAlign: "center" }}>
      <Box sx={{ py: "24px", backgroundColor: blue[500] }}>
        <img
          src={LSLogoStretched}
          style={{ maxHeight: "60px", maxHeigh: "100%" }}
          alt="LinkStep Logo"
        />
      </Box>
      <Divider />
      <List>
        {items?.map((item) =>
          item.subItems ? (
            <Box key={`sub-item-2-${item.name}`}>
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleSideMenuItemClick(item)}>
                  {item.icon}
                  <ListItemText sx={{ marginLeft: 2 }} primary={item.name} />
                  {activeSideMenuItem === item ? (
                    <ExpandLess />
                  ) : (
                    <ExpandMore />
                  )}
                </ListItemButton>
              </ListItem>
              <Collapse in={activeSideMenuItem?.name === item?.name}>
                {item.subItems?.map((subItem) => {
                  const hasPermissionToSub =
                    permissions[subItem.permissionName] ||
                    permissions[subItem.permissionName] === undefined;
                  return (
                    hasPermissionToSub && (
                      <ListItemButton
                        onClick={() => handleNavigate(subItem.path)}
                        sx={{
                          marginLeft: 2,
                          backgroundColor:
                            location.pathname === subItem.path
                              ? yellow[100]
                              : "inherit",
                        }}
                        key={`sub-item-3-${subItem.path}`}
                      >
                        {subItem.icon}
                        <ListItemText
                          sx={{ marginLeft: 2 }}
                          primary={subItem.name}
                        />
                      </ListItemButton>
                    )
                  );
                })}
              </Collapse>
            </Box>
          ) : (
            <ListItem key={item.name} disablePadding>
              <ListItemButton
                onClick={() => handleNavigate(item.path)}
                sx={{
                  backgroundColor:
                    location.pathname === item.path ? yellow[100] : "inherit",
                }}
              >
                {item.icon}
                <ListItemText sx={{ marginLeft: 2 }} primary={item.name} />
              </ListItemButton>
            </ListItem>
          ),
        )}
        {isMobile && (
          <ListItem key="logout" disablePadding>
            <ListItemButton onClick={handleLogout}>
              <Person />
              <ListItemText sx={{ marginLeft: 2 }} primary="Logout" />
            </ListItemButton>
          </ListItem>
        )}
      </List>
    </Box>
  );

  const sideBar = (
    <Box
      onClick={handleDrawerToggle}
      sx={{
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        flex: 1,
      }}
    >
      <List>
        {appPages?.map((item) =>
          item.subItems ? (
            <Box key={`sub-item-2-${item.name}`}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleSideMenuItemClick(item)}
                  sx={{
                    backgroundColor:
                      location.pathname === item.path ? yellow[100] : "inherit",
                  }}
                >
                  {item.icon}
                  <ListItemText sx={{ marginLeft: 2 }} primary={item.name} />
                  {activeSideMenuItem === item ? (
                    <ExpandLess />
                  ) : (
                    <ExpandMore />
                  )}
                </ListItemButton>
              </ListItem>
              <Collapse in={activeSideMenuItem?.name === item?.name}>
                {item.subItems?.map((subItem) => {
                  const hasPermissionToSub =
                    permissions[subItem.permissionName] ||
                    permissions[subItem.permissionName] === undefined;
                  return (
                    hasPermissionToSub && (
                      <ListItemButton
                        onClick={() => handleNavigate(subItem.path)}
                        sx={{
                          marginLeft: 2,
                          backgroundColor:
                            location.pathname === subItem.path
                              ? yellow[100]
                              : "inherit",
                        }}
                        key={`sub-item-3-${subItem.path}`}
                      >
                        {subItem.icon}
                        <ListItemText
                          sx={{ marginLeft: 2 }}
                          primary={subItem.name}
                        />
                      </ListItemButton>
                    )
                  );
                })}
              </Collapse>
            </Box>
          ) : (
            <ListItem key={item.name} disablePadding>
              <ListItemButton
                onClick={() => handleNavigate(item.path)}
                sx={{
                  backgroundColor:
                    location.pathname === item.path ? yellow[100] : "inherit",
                }}
              >
                {item.icon}
                <ListItemText sx={{ marginLeft: 2 }} primary={item.name} />
              </ListItemButton>
            </ListItem>
          ),
        )}
      </List>

      <Box
        style={{
          display: "flex",
          flexDirection: "row",
          flex: 1,
          justifyContent: "center",
          alignItems: "end",
        }}
      >
        {(permissions.canImpersonate || impersonation.user) && (
          <Button
            variant="outlined"
            sx={{ marginX: "16px", height: "40px", width: "100%" }}
            onClick={handleImpersonateClick}
          >
            Impersonation
          </Button>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          marginTop: "32px",
          marginBottom: "80px",
          flexDirection: "column",
          justifyContent: "start",
          textAlign: "left",
          marginX: 2,
        }}
      >
        {usersStatus === "ready" &&
          users?.filter((c) => c.role === "SuperUser")?.length > 0 && (
          <a
            href={`mailto:${users
              ?.filter((c) => c.role === "SuperUser")
              ?.map((obj) => obj.email)
              ?.join(",")}`}
          >
            Contact Administrators
          </a>
        )}
        <Link to={urlRoutes.termsOfService}>Terms of Service</Link>
        <Link to={urlRoutes.privacy}>Privacy Policy</Link>
      </Box>
    </Box>
  );

  const profileControl = permissions.userUUID ? (
    <>
      <IconButton
        onClick={handleOpenProfile}
        aria-label="Open profile menu"
        sx={{ p: 0.5 }}
      >
        {user.savedFileUUID || user.profilePhotoUUID ? (
          <UserAvatar user={user} size={40} />
        ) : (
          <Box
            sx={{
              height: "40px",
              width: "40px",
              backgroundColor: "lightgrey",
              borderRadius: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" color="#555" sx={{ fontWeight: 600 }}>
              {`${user?.firstName?.[0] || ""}${user?.lastName?.[0] || ""}`}
            </Typography>
          </Box>
        )}
      </IconButton>
      <MuiMenu
        anchorEl={profileAnchorEl}
        open={profileMenuOpen}
        onClose={handleCloseProfile}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={openVendor}>My Profile</MenuItem>
        <MenuItem onClick={handleLogout}>Logout</MenuItem>
      </MuiMenu>
    </>
  ) : (
    <Button onClick={() => navigate(urlRoutes.login)} sx={{ color: "#fff" }}>
      Login
    </Button>
  );

  return (
    <Box sx={{ display: "flex", width: "100%" }}>
      <AppBar component="nav" sx={{ backgroundColor: "#080F5B" }}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: "none" } }}
          >
            <Menu />
          </IconButton>
          <Box
            onClick={() => handleNavigate(urlRoutes.incidentReports)}
            sx={{ display: "block", cursor: "pointer" }}
          >
            <img
              src={LSLogoStretched}
              style={{ height: "40px", width: "190px" }}
              alt="LinkStep Logo"
            />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "row", flex: 1 }} />
          <Box
            sx={{
              display: {
                xs: "none",
                sm: "flex",
                flexDirection: "row",
                alignItems: "center",
              },
            }}
          >
            {items?.map((item) => (
              <Box
                sx={{ display: "block", position: "relative" }}
                onMouseOver={() => handleMouseEnter(item)}
                onMouseLeave={handleMouseLeave}
                key={`item-1-${item.name}`}
              >
                <Button
                  onClick={() => handleNavigate(item.path)}
                  sx={{ color: "#fff" }}
                >
                  {item.name}
                </Button>
                {item.subItems && (
                  <Paper
                    elevation={4}
                    sx={{
                      display:
                        activeMenuItem?.name === item.name ? "block" : "none",
                      minWidth: "150px",
                      position: "absolute",
                      padding: 1,
                      backgroundColor: "lightGray",
                    }}
                  >
                    <List dense>
                      {item.subItems?.map((subItem) => {
                        const hasPermissionToSub =
                          permissions[subItem.permissionName] ||
                          permissions[subItem.permissionName] === undefined;
                        return (
                          hasPermissionToSub && (
                            <Link
                              key={`${subItem.name}-${subItem.path}`}
                              style={{ color: "black", textDecoration: "none" }}
                              to={subItem.path}
                            >
                              <ListItem sx={{ p: 0 }}>
                                <ListItemButton sx={{ py: 0.5, px: 1 }}>
                                  <ListItemText
                                    sx={{ my: 0, width: "100%" }}
                                    primary={subItem.name}
                                  />
                                </ListItemButton>
                              </ListItem>
                            </Link>
                          )
                        );
                      })}
                    </List>
                  </Paper>
                )}
              </Box>
            ))}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
            {permissions.userUUID && <InboxBell />}
            {profileControl}
          </Box>
        </Toolbar>
      </AppBar>
      <nav>
        <Drawer
          variant={isMobile ? "temporary" : "permanent"}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: isTablet ? drawerWidthSmall : drawerWidthLarge,
            },
          }}
        >
          {drawer}
        </Drawer>
      </nav>
      <Box
        component="main"
        sx={{
          display: "flex",
          flexDirection: "row",
          width: "100%",
          position: "relative",
        }}
      >
        <Toolbar />
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            flex: 1,
            marginTop: "64px",
            marginLeft: showSideMenu ? 0 : "-64px",
          }}
        >
          {showSideMenu && (
            <Drawer
              variant={isMobile ? "temporary" : "permanent"}
              open={isMobile ? mobileOpen : true}
              onClose={handleDrawerToggle}
              ModalProps={{ keepMounted: true }}
              sx={{
                display: { xs: "none", sm: "fixed" },
                "& .MuiDrawer-paper": {
                  boxSizing: "border-box",
                  width: isTablet ? drawerWidthSmall : drawerWidthLarge,
                  marginTop: "64px",
                },
              }}
            >
              {sideBar}
            </Drawer>
          )}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              marginLeft:
                isMobile && !showSideMenu
                  ? 4
                  : isMobile
                    ? -2
                    : `${
                        showSideMenu
                          ? isTablet
                            ? drawerWidthSmall - 48
                            : drawerWidthLarge - 48
                          : 16
                      }px`,
            }}
          >
            <Box sx={{ padding: topComponents.length > 0 ? 2 : 0 }}>
              {topComponents}
            </Box>
            <Box sx={{ display: "flex", flexDirection: "row" }}>
              <Suspense
                fallback={
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      width: "100%",
                      py: 8,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                }
              >
                {user?.uuid || accessiblePage ? children : <Login />}
              </Suspense>
            </Box>
          </Box>
        </Box>
      </Box>
      {(impersonation.company || impersonation.user) && (
        <Paper
          sx={{
            position: "fixed",
            bottom: 20,
            right: 20,
            backgroundColor: green[400],
            p: 3,
            textAlign: "center",
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            {impersonation.user === customAccessLabel
              ? "Previewing access"
              : "Impersonating"}
          </Typography>
          {impersonation.company && (
            <Typography variant="body1" fontWeight={600}>
              {impersonation.company}
            </Typography>
          )}
          {impersonation.user && impersonation.user !== customAccessLabel && (
            <Typography variant="body1" fontWeight={600}>
              {impersonation.user}
            </Typography>
          )}
          <Button
            variant="outlined"
            size="small"
            sx={{ mt: 1.5 }}
            onClick={handleImpersonateClick}
          >
            Change
          </Button>
        </Paper>
      )}
      <GlobalModals />
      {impersonationOpen && (
        <ImpersonationModal
          open
          user={user}
          users={users}
          permissions={permissions}
          impersonation={impersonation}
          onClose={() => setImpersonationOpen(false)}
          dispatch={dispatch}
        />
      )}
    </Box>
  );
}

export default AppLayout;
